import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './RequestAccessPage.css';

/**
 * Request Access page - Public page for prospective tenants to request access.
 */
export const RequestAccessPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    organization_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    intended_use: '',
    expected_clients: '',
    expected_staff: '',
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Combine first and last name for backend
      const primary_contact_name = `${formData.first_name} ${formData.last_name}`.trim();

      const payload = {
        organization_name: formData.organization_name,
        primary_contact_name: primary_contact_name,
        email: formData.email,
        phone: formData.phone || undefined,
        intended_use: formData.intended_use,
        expected_clients: formData.expected_clients ? parseInt(formData.expected_clients) : undefined,
        expected_staff: formData.expected_staff ? parseInt(formData.expected_staff) : undefined,
      };

      await api.post('/v1/access-requests', payload);
      setSubmitted(true);
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Failed to submit access request. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="request-access-page">
        <div className="request-access-container">
          <div className="request-access-header">
            <h1>Request Submitted</h1>
            <p>Thank you for your interest in Portalr</p>
          </div>
          <div className="success-message">
            <p>
              Your access request has been submitted successfully. 
              Our team will review your request and notify you via email once a decision has been made.
            </p>
            <p>
              This process typically takes 1-2 business days.
            </p>
          </div>
          <button
            className="btn-primary"
            onClick={() => navigate('/login')}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="request-access-page">
      <div className="request-access-container">
        <div className="request-access-header">
          <h1>Request Access to Portalr</h1>
          <p>Complete the form below to request access to Portalr</p>
        </div>

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="request-access-form">
          <div className="form-group">
            <label htmlFor="organization_name">
              Organization / Firm Name <span className="required">*</span>
            </label>
            <input
              id="organization_name"
              type="text"
              value={formData.organization_name}
              onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
              required
              placeholder="Your organization name"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">
                First Name <span className="required">*</span>
              </label>
              <input
                id="first_name"
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
                placeholder="John"
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">
                Last Name <span className="required">*</span>
              </label>
              <input
                id="last_name"
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email Address <span className="required">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              autoComplete="email"
              placeholder="you@example.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number (Optional)</label>
            <input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="form-group">
            <label htmlFor="intended_use">
              Intended Use <span className="required">*</span>
            </label>
            <textarea
              id="intended_use"
              value={formData.intended_use}
              onChange={(e) => setFormData({ ...formData, intended_use: e.target.value })}
              required
              rows={4}
              placeholder="Please describe how you plan to use Portalr..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="expected_clients">Expected Number of Clients (Optional)</label>
              <input
                id="expected_clients"
                type="number"
                min="0"
                value={formData.expected_clients}
                onChange={(e) => setFormData({ ...formData, expected_clients: e.target.value })}
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="expected_staff">Expected Number of Staff (Optional)</label>
              <input
                id="expected_staff"
                type="number"
                min="0"
                value={formData.expected_staff}
                onChange={(e) => setFormData({ ...formData, expected_staff: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>

          <div className="info-box">
            <p>
              <strong>Note:</strong> All access requests are reviewed by our team. 
              You will receive an email notification once your request has been reviewed.
            </p>
          </div>

          <button
            type="submit"
            className="btn-primary btn-submit"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>

          <div className="form-footer">
            <p>
              Already have an account?{' '}
              <a href="/login" onClick={(e) => {
                e.preventDefault();
                navigate('/login');
              }}>
                Sign in
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
